import { Component } from "@fingerprintjs/fingerprintjs";

const GET_ICE_CANDIDATE_TIMEOUT = 500; // Milliseconds

// Only Media Device kind for now since other attributes are too volatile
interface MediaDeviceInfo {
    kind: string;
}

interface MediaCapabilities {
    audio: Record<string, unknown>[] | null;
    video: Record<string, unknown>[] | null;
}

interface IceCandidateData {
    iceCandidateData: string | null; // First Ice Candidate found with foundation, component, protocol and priority
    mediaCapabilities: MediaCapabilities | null; // Inferred media capabilities from SDP data
}

// Interface for WebRTC component value
export interface WebRTCComponent extends IceCandidateData {
    mediaDevices: MediaDeviceInfo[];
}

interface RtxCounter {
    increment: () => void;
    getValue: () => number;
}

async function getMediaDevices(): Promise<MediaDeviceInfo[] | null> {
    // Media Devices API might not be available
    if (!navigator?.mediaDevices?.enumerateDevices) return null;

    const mediaDevices = await navigator.mediaDevices.enumerateDevices().catch(() => {
        return null;
    });

    let mediaDeviceFp: MediaDeviceInfo[] | null = null;

    if (mediaDevices) {
        mediaDeviceFp = mediaDevices.map((device) => ({
                kind: device.kind,
        })).sort((a, b) => {
            return a.kind.localeCompare(b.kind, "en");
        });
    }

    return mediaDeviceFp;
}

// Tries to parse the connection SDP raw data to fetch IP address
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getIpAddressFromSDP(sdp: string): string | null {
    const ipUnknown = "0.0.0.0";
    const candidateEncoding = /((udp|tcp)\s)((\d|\w)+\s)((\d|\w|(\.|:))+)(?=\s)/ig;
    const lineEncoding = /(c=IN\s)(.+)\s/ig;
    const lineIpAddress = ((sdp.match(lineEncoding) || [])[0] || "").trim().split(" ")[2];
    if (lineIpAddress && (lineIpAddress != ipUnknown)) {
        return lineIpAddress;
    }
    const candidateIpAddress = ((sdp.match(candidateEncoding) || [])[0] || "").split(" ")[2];
    return candidateIpAddress && (candidateIpAddress != ipUnknown) ? candidateIpAddress : null;
}

function constructMediaDescriptor(mediaType: string, sdp: string, sdpDescriptors: string[], rtxCounter: RtxCounter): Record<string, unknown>[] | null {
    if (!(""+sdpDescriptors)) {
		return null;
	}

	return sdpDescriptors.reduce((descriptionAcc: Record<string, unknown>[], descriptor: string) => {
		const matcher = `(rtpmap|fmtp|rtcp-fb):${descriptor} (.+)`;
		const formats = (sdp.match(new RegExp(matcher, "g")) || []) as Array<string>;
		if (!(""+formats)) {
			return descriptionAcc;
		}
		const isRtxCodec = ("" + formats).includes(" rtx/");
		if (isRtxCodec) {
			if (rtxCounter.getValue()) {
				return descriptionAcc;
			}
			rtxCounter.increment();
		}
		const getLineData = (x: string) => x.replace(/[^\s]+ /, "");
		const description = formats.reduce((acc: Record<string, unknown>, x: string) => {
			const rawData = getLineData(x);
			const data = rawData.split("/");
			const codec = data[0];
			const description: Record<string, unknown> = {};

			if (x.includes("rtpmap")) {
				if (mediaType == "audio") {
					description.channels = (+data[2]) || 1;
				}
				description.mimeType = `${mediaType}/${codec}`;
				description.clockRates = [+data[1]];
				return {
					...acc,
					...description,
				};
			} else if (x.includes("rtcp-fb")) {
				return {
					...acc,
					feedbackSupport: [...(acc.feedbackSupport as string[]||[]), rawData],
				};
			} else if (isRtxCodec) {
				return acc; // no sdpFmtpLine
			}
			return { ...acc, sdpFmtpLine: [...rawData.split(";")] };
		}, {});

		let shouldMerge = false;
		const mergerAcc = descriptionAcc.map((x) => {
			shouldMerge = x.mimeType == description.mimeType;
			if (shouldMerge) {
				if (x.feedbackSupport) {
					x.feedbackSupport = [
						...new Set([...x.feedbackSupport as string[], ...description.feedbackSupport as string[]]),
					];
				}
				if (x.sdpFmtpLine) {
					x.sdpFmtpLine = [
						...new Set([...x.sdpFmtpLine as string[], ...description.sdpFmtpLine as string[]]),
					];
				}
				return {
					...x,
					clockRates: [
						...new Set([...x.clockRates as number[], ...description.clockRates as number[]]),
					],
				};
			}
			return x;
		});
		if (shouldMerge) {
			return mergerAcc;
		}
		return [...descriptionAcc, description];
	}, [] as Record<string, unknown>[]);
}

// Get audio and video capabilites from the SDP data
function getCapabilities(sdp: string): MediaCapabilities | null {
    if (!sdp) return null;

	const videoDescriptors = ((/m=video [^\s]+ [^\s]+ ([^\n|\r]+)/.exec(sdp) || [])[1] || "").split(" ");
	const audioDescriptors = ((/m=audio [^\s]+ [^\s]+ ([^\n|\r]+)/.exec(sdp) || [])[1] || "").split(" ");

    const createCounter = () => {
        let counter = 0;
        return {
            increment: () => counter += 1,
            getValue: () => counter,
        };
    };
    
	const rtxCounter = createCounter();
	return {
		audio: constructMediaDescriptor(
			"audio",
			sdp,
			audioDescriptors,
			rtxCounter,
		),
		video: constructMediaDescriptor(
			"video",
			sdp,
			videoDescriptors,
			rtxCounter,
		),
	};
}

// Retrieves fingerprint signals from WebRTC API
async function getWebRTCData(): Promise<IceCandidateData | null> {
    // API might not be available
    if (!window.RTCPeerConnection) return null;
    
    // Instantiate a RTC Perr Connection and wait for event with IceCandidate
    const rtcConnection = new RTCPeerConnection({ 
        iceCandidatePoolSize: 1,
        iceServers: []
    });
    rtcConnection.createDataChannel("");

	const options = { offerToReceiveAudio: true, offerToReceiveVideo: true };
    const offer = await rtcConnection.createOffer(options as RTCOfferOptions);
    rtcConnection.setLocalDescription(offer);

    // From offer SDP raw data, infer audio and video capabilites
    const sdp = offer.sdp ? offer.sdp : "";

    const mediaCapabilities = getCapabilities(sdp);

    // Return a promise that will resolve when we find the IceCandidate or we reach timeout
    return new Promise((resolve) => {
        // Function to set a limit for how long we are willing to wait to find the IceCandidate
        const giveUp = setTimeout(() => {
            // Clean up and simply return null
			rtcConnection.removeEventListener("icecandidate", onCandidateFound);
			rtcConnection.close();
			return resolve(
                {
                    iceCandidateData: null,
                    mediaCapabilities: mediaCapabilities
                }
            );
		}, GET_ICE_CANDIDATE_TIMEOUT);

        // IceCandidate event handler
        // Consider only candidates with a valid found address
        const onCandidateFound = (event: RTCPeerConnectionIceEvent) => {
            if (!event.candidate || !event.candidate.candidate) return; // Ignore candidates with empty data

            // Get some information from candidate and join into a single value
            const candidateData = event.candidate.candidate.split(" ");
            
            const component = candidateData[1];
            const protocol = candidateData[2];
            const priority = candidateData[3]; 

            // Cleanup and return found IceCandidate data
            rtcConnection.removeEventListener("icecandidate", onCandidateFound);
			clearTimeout(giveUp);
			rtcConnection.close();
            resolve({
                iceCandidateData: [component, protocol, priority].join("-"),
                mediaCapabilities: mediaCapabilities
            });
        };

        rtcConnection.addEventListener("icecandidate", onCandidateFound);
    });
}

// This is a component calculate by using WebRTC related APIs
// It will be compromised of 2 main parts: media devices and ICE candidates data
export async function GetWebRTCComponent(): Promise<Component<WebRTCComponent>> {
    // Get both media device and IceCandidate data in parallel
    let error: Error | undefined = undefined;

    const startTime = Date.now();
    const result = await Promise.all([getMediaDevices(), getWebRTCData()]).catch((e) => {
        error = e as Error; 
        return undefined; 
    });

    const duration = Date.now() - startTime;

    if (result) {
        const componentValue = {
            mediaDevices: result[0] ? result[0] : [],
            iceCandidateData: result[1] ? result[1].iceCandidateData : null,
            mediaCapabilities: result[1] ? result[1].mediaCapabilities : null
        } as WebRTCComponent;

        return {
            value: componentValue,
            duration: duration,
        };
    }

    return {
        duration: duration,
        error: error ? error : new Error("Unknown error"),
    };
}